import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, Upload, FileSpreadsheet, Loader2, X } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sabit belge türleri
const STATIC_DOCUMENT_TYPES = [
  // Firma Belgeleri
  { value: 'sgk_hizmet_listesi', label: 'SGK Hizmet Listesi', category: 'Firma' },
  { value: 'isg_katip_uzman', label: 'İSG Katip Uzman', category: 'Firma' },
  { value: 'isg_katip_hekim', label: 'İSG Katip Hekim', category: 'Firma' },
  // Personel Belgeleri
  { value: 'sgk_ise_giris', label: 'SGK İşe Giriş Bildirgesi', category: 'Personel' },
  { value: 'isg_egitimi', label: 'İSG Eğitimi', category: 'Personel' },
  { value: 'saglik_raporu', label: 'Sağlık Raporu', category: 'Personel' },
  { value: 'mesleki_yeterlilik', label: 'Mesleki Yeterlilik', category: 'Personel' },
  { value: 'mesleki_egitim', label: 'Mesleki Eğitim', category: 'Personel' },
  { value: 'kkd_zimmet', label: 'KKD Zimmet', category: 'Personel' },
];

const TemplatesPage = () => {
  const { canCreate, canEdit, canDelete } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('all');
  const [formData, setFormData] = useState({ 
    document_type: '',
    template_text: ''
  });
  
  // MYK List state
  const [mykList, setMykList] = useState([]);
  const [mykUploading, setMykUploading] = useState(false);
  const mykFileInputRef = useRef(null);
  
  // Operator List state
  const [operatorList, setOperatorList] = useState([]);
  const [operatorUploading, setOperatorUploading] = useState(false);
  const operatorFileInputRef = useRef(null);
  const [newOperatorName, setNewOperatorName] = useState('');

  useEffect(() => {
    fetchTemplates();
    fetchMykList();
    fetchOperatorList();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/deficiency-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Şablonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchMykList = async () => {
    try {
      const response = await axios.get(`${API}/myk-list`);
      setMykList(response.data);
    } catch (error) {
      console.error('MYK list fetch error');
    }
  };

  const fetchOperatorList = async () => {
    try {
      const response = await axios.get(`${API}/operator-list`);
      setOperatorList(response.data);
    } catch (error) {
      console.error('Operator list fetch error');
    }
  };

  const handleMykUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setMykUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/myk-list/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchMykList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setMykUploading(false);
      if (mykFileInputRef.current) {
        mykFileInputRef.current.value = '';
      }
    }
  };

  const handleOperatorUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setOperatorUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/operator-list/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchOperatorList();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yukleme basarisiz');
    } finally {
      setOperatorUploading(false);
      if (operatorFileInputRef.current) {
        operatorFileInputRef.current.value = '';
      }
    }
  };

  const handleAddOperator = async () => {
    if (!newOperatorName.trim()) return;
    try {
      await axios.post(`${API}/operator-list`, { name: newOperatorName.trim() });
      toast.success('Operator belgesi eklendi');
      setNewOperatorName('');
      fetchOperatorList();
    } catch (error) {
      toast.error('Ekleme basarisiz');
    }
  };

  const handleDeleteOperator = async (name) => {
    try {
      await axios.delete(`${API}/operator-list/${encodeURIComponent(name)}`);
      toast.success('Operator belgesi silindi');
      fetchOperatorList();
    } catch (error) {
      toast.error('Silme basarisiz');
    }
  };

  const handleClearOperatorList = async () => {
    if (!(await confirmDialog({ title: 'Operatör listesi silinsin mi?', description: 'Tüm operatör listesini silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/operator-list`);
      toast.success('Operator listesi temizlendi');
      setOperatorList([]);
      fetchOperatorList();
    } catch (error) {
      toast.error('Silme basarisiz');
    }
  };

  const handleClearMykList = async () => {
    if (!(await confirmDialog({ title: 'MYK listesi silinsin mi?', description: 'Tüm MYK listesini silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/myk-list`);
      toast.success('MYK listesi temizlendi');
      setMykList([]);
      fetchMykList();
    } catch (error) {
      toast.error('Silme başarısız');
      console.error('MYK delete error:', error);
    }
  };

  // Dinamik belge türleri - ekipman belgeleri (sabit, alt türden bağımsız)
  const DOCUMENT_TYPES = [
    ...STATIC_DOCUMENT_TYPES,
    // Araç belgeleri (sabit - tüm araç alt türleri için aynı)
    { value: 'ekipman_arac_operatorluk', label: 'Operatörlük Belgesi', category: 'Ekipman - Araç' },
    { value: 'ekipman_arac_periyodik', label: 'Periyodik Muayene', category: 'Ekipman - Araç' },
    { value: 'ekipman_arac_mali', label: 'Mali Mesuliyet Sigortası', category: 'Ekipman - Araç' },
    // İş Ekipmanı belgeleri (sabit - tüm iş ekipmanı alt türleri için aynı)
    { value: 'ekipman_is_periyodik', label: 'Periyodik Muayene', category: 'Ekipman - İş Ekipmanı' },
  ];

  const getDocTypeLabel = (docType) => {
    const type = DOCUMENT_TYPES.find(t => t.value === docType);
    return type?.label || docType;
  };

  const getDocTypeCategory = (docType) => {
    const type = DOCUMENT_TYPES.find(t => t.value === docType);
    return type?.category || '';
  };

  // Kategorilere göre grupla
  const categorizedTypes = DOCUMENT_TYPES.reduce((acc, type) => {
    if (!acc[type.category]) acc[type.category] = [];
    acc[type.category].push(type);
    return acc;
  }, {});

  const filteredTemplates = selectedTypeFilter === 'all' 
    ? templates 
    : templates.filter(t => t.document_type === selectedTypeFilter);

  const handleOpenDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setFormData({ 
        document_type: template.document_type,
        template_text: template.template_text
      });
    } else {
      setEditingTemplate(null);
      setFormData({ 
        document_type: '',
        template_text: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/deficiency-templates/${editingTemplate.id}`, formData);
        toast.success('Şablon güncellendi');
      } else {
        await axios.post(`${API}/deficiency-templates`, formData);
        toast.success('Şablon eklendi');
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog({ title: 'Şablon silinsin mi?', description: 'Bu şablonu silmek istediğinizden emin misiniz?', confirmLabel: 'Evet, Sil' }))) return;
    try {
      await axios.delete(`${API}/deficiency-templates/${id}`);
      toast.success('Şablon silindi');
      fetchTemplates();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="templates-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Eksiklik Şablonları</h1>
          <p className="text-slate-500 mt-1">Belge eksiklikleri için hazır metin şablonları</p>
        </div>
        {canCreate('templates') && (
          <Button 
            onClick={() => handleOpenDialog()}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-template-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Şablon Ekle
          </Button>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label>Belge Türü:</Label>
        <Select value={selectedTypeFilter} onValueChange={setSelectedTypeFilter}>
          <SelectTrigger className="w-64" data-testid="template-type-filter">
            <SelectValue placeholder="Belge türü seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Belgeler</SelectItem>
            <SelectItem value="divider-firma" disabled>─ Firma Belgeleri ─</SelectItem>
            {DOCUMENT_TYPES.filter(t => t.category === 'Firma').map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
            <SelectItem value="divider-personel" disabled>─ Personel Belgeleri ─</SelectItem>
            {DOCUMENT_TYPES.filter(t => t.category === 'Personel').map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
            {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - Araç').length > 0 && (
              <>
                <SelectItem value="divider-ekipman-arac" disabled className="text-blue-500">─ Ekipman - Araç ─</SelectItem>
                {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - Araç').map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </>
            )}
            {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - İş Ekipmanı').length > 0 && (
              <>
                <SelectItem value="divider-ekipman-is" disabled className="text-orange-500">─ Ekipman - İş Ekipmanı ─</SelectItem>
                {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - İş Ekipmanı').map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Belge Türü</TableHead>
                <TableHead className="font-semibold">Kategori</TableHead>
                <TableHead className="font-semibold">Eksiklik Metni</TableHead>
                <TableHead className="font-semibold text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <FileText className="h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">Henüz şablon eklenmemiş</p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleOpenDialog()}
                      >
                        İlk şablonu ekle
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => (
                  <TableRow key={template.id} className="table-row-hover">
                    <TableCell className="font-medium">
                      {getDocTypeLabel(template.document_type)}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        getDocTypeCategory(template.document_type) === 'Firma' 
                          ? 'bg-blue-100 text-blue-700' 
                          : getDocTypeCategory(template.document_type) === 'Personel'
                          ? 'bg-purple-100 text-purple-700'
                          : getDocTypeCategory(template.document_type).includes('Araç')
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-orange-100 text-orange-700'
                      }>
                        {getDocTypeCategory(template.document_type)}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-md truncate">
                      {template.template_text}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit('templates') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(template)}
                            data-testid={`edit-template-${template.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canDelete('templates') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(template.id)}
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            data-testid={`delete-template-${template.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Şablon Düzenle' : 'Yeni Şablon'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Belge Türü</Label>
                <Select
                  value={formData.document_type}
                  onValueChange={(value) => setFormData({ ...formData, document_type: value })}
                  required
                >
                  <SelectTrigger data-testid="template-doc-type-select">
                    <SelectValue placeholder="Belge türü seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="header-firma" disabled className="font-semibold text-slate-400">
                      ─ Firma Belgeleri ─
                    </SelectItem>
                    {DOCUMENT_TYPES.filter(t => t.category === 'Firma').map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="header-personel" disabled className="font-semibold text-slate-400">
                      ─ Personel Belgeleri ─
                    </SelectItem>
                    {DOCUMENT_TYPES.filter(t => t.category === 'Personel').map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                    {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - Araç').length > 0 && (
                      <>
                        <SelectItem value="header-ekipman-arac" disabled className="font-semibold text-blue-500">
                          ─ Ekipman - Araç ─
                        </SelectItem>
                        {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - Araç').map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - İş Ekipmanı').length > 0 && (
                      <>
                        <SelectItem value="header-ekipman-is" disabled className="font-semibold text-orange-500">
                          ─ Ekipman - İş Ekipmanı ─
                        </SelectItem>
                        {DOCUMENT_TYPES.filter(t => t.category === 'Ekipman - İş Ekipmanı').map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Eksiklik Metni</Label>
                <Input
                  value={formData.template_text}
                  onChange={(e) => setFormData({ ...formData, template_text: e.target.value })}
                  placeholder="Örn: Belge tarihi geçersiz"
                  required
                  data-testid="template-text-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-template-btn">
                {editingTemplate ? 'Güncelle' : 'Ekle'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MYK List Section */}
      <Card className="border-slate-200 mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5" />
            Mesleki Yeterlilik (MYK) Listesi
          </CardTitle>
          <CardDescription>
            Personel belgelerinde kullanılacak MYK sertifika listesini Excel dosyasından yükleyin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="file"
              ref={mykFileInputRef}
              onChange={handleMykUpload}
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="myk-file-input"
            />
            <Button
              onClick={() => mykFileInputRef.current?.click()}
              variant="outline"
              disabled={mykUploading}
              data-testid="myk-upload-btn"
            >
              {mykUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Yükleniyor...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Excel Yükle
                </>
              )}
            </Button>
            {mykList.length > 0 && (
              <Button
                onClick={handleClearMykList}
                variant="outline"
                className="text-rose-600 hover:bg-rose-50"
                data-testid="myk-clear-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Listeyi Temizle
              </Button>
            )}
            <span className="text-sm text-slate-500">
              {mykList.length > 0 ? `${mykList.length} MYK belgesi yüklü` : 'Henüz liste yüklenmemiş'}
            </span>
          </div>
          
          {mykList.length > 0 && (
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-12">#</TableHead>
                    <TableHead className="font-semibold">Belge Adı</TableHead>
                    <TableHead className="font-semibold">Kod</TableHead>
                    <TableHead className="font-semibold">Açıklama</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mykList.map((item, idx) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-slate-400">{idx + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell className="text-slate-600">{item.code || '-'}</TableCell>
                      <TableCell className="text-slate-600">{item.description || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Operator List Section */}
      <Card className="border-slate-200 mt-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5" />
            Operator Belgesi Listesi
          </CardTitle>
          <CardDescription>
            Arac ekipmanlarinda kullanilacak operator belgesi listesini Excel ile yukleyin veya elle girin
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <input
              type="file"
              ref={operatorFileInputRef}
              onChange={handleOperatorUpload}
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="operator-file-input"
            />
            <Button
              onClick={() => operatorFileInputRef.current?.click()}
              variant="outline"
              disabled={operatorUploading}
              data-testid="operator-upload-btn"
            >
              {operatorUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Yukleniyor...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Excel Yukle
                </>
              )}
            </Button>
            {operatorList.length > 0 && (
              <Button
                onClick={handleClearOperatorList}
                variant="outline"
                className="text-rose-600 hover:bg-rose-50"
                data-testid="operator-clear-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Listeyi Temizle
              </Button>
            )}
            <span className="text-sm text-slate-500">
              {operatorList.length > 0 ? `${operatorList.length} operator belgesi yuklu` : 'Henuz liste yuklenmemis'}
            </span>
          </div>
          
          {/* Elle ekleme */}
          <div className="flex items-center gap-2">
            <Input
              value={newOperatorName}
              onChange={(e) => setNewOperatorName(e.target.value)}
              placeholder="Operator belgesi adi (elle ekle)"
              className="max-w-sm"
              data-testid="operator-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddOperator();
                }
              }}
            />
            <Button
              onClick={handleAddOperator}
              variant="outline"
              disabled={!newOperatorName.trim()}
              data-testid="operator-add-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ekle
            </Button>
          </div>
          
          {operatorList.length > 0 && (
            <div className="border rounded-lg max-h-64 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-12">#</TableHead>
                    <TableHead className="font-semibold">Operator Belgesi Adi</TableHead>
                    <TableHead className="font-semibold w-20">Islem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {operatorList.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-slate-400">{idx + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-rose-600 hover:text-rose-700 h-7 w-7 p-0"
                          onClick={() => handleDeleteOperator(item.name)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TemplatesPage;
